import React, { useMemo, useState } from "react";
import { MapPin } from "lucide-react";

// A stylized SVG map placeholder that projects pinned lat/lng onto a canvas
// and connects them per day with a route polyline.
export default function MapView({ trip }) {
  const [activeDay, setActiveDay] = useState(trip.days[0]?.day_number || 1);
  const day = trip.days.find(d => d.day_number === activeDay) || trip.days[0];
  const pinned = (day?.activities || []).filter(a => a.lat != null && a.lng != null);

  const { points, bounds } = useMemo(() => {
    if (pinned.length === 0) return { points: [], bounds: null };
    let minLat=90, maxLat=-90, minLng=180, maxLng=-180;
    pinned.forEach(a => {
      minLat = Math.min(minLat, a.lat); maxLat = Math.max(maxLat, a.lat);
      minLng = Math.min(minLng, a.lng); maxLng = Math.max(maxLng, a.lng);
    });
    // pad
    const padLat = Math.max(0.01, (maxLat - minLat) * 0.3) || 0.02;
    const padLng = Math.max(0.01, (maxLng - minLng) * 0.3) || 0.02;
    minLat -= padLat; maxLat += padLat; minLng -= padLng; maxLng += padLng;
    const w = 800, h = 500;
    const points = pinned.map(a => ({
      ...a,
      x: ((a.lng - minLng) / (maxLng - minLng)) * w,
      y: (1 - (a.lat - minLat) / (maxLat - minLat)) * h,
    }));
    return { points, bounds: { minLat, maxLat, minLng, maxLng } };
  }, [pinned]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif-display text-2xl font-semibold">Route map</h2>
        <div className="flex gap-1 overflow-x-auto">
          {trip.days.map(d => (
            <button key={d.day_number} onClick={()=>setActiveDay(d.day_number)}
              data-testid={`map-day-${d.day_number}`}
              className={`px-3 py-1.5 rounded-full font-mono-code text-xs uppercase tracking-wider border ${activeDay===d.day_number?"bg-stone-900 text-white border-stone-900":"bg-white border-stone-300 text-stone-700"}`}>
              Day {d.day_number}
            </button>
          ))}
        </div>
      </div>

      <div className="relative rounded-2xl border border-stone-200 overflow-hidden bg-gradient-to-br from-emerald-50 to-sky-50">
        <div className="absolute inset-0 grain-bg opacity-40 pointer-events-none"/>
        <svg viewBox="0 0 800 500" className="w-full h-[500px]" data-testid="map-svg">
          {/* stylized grid */}
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#d1c7b7" strokeWidth="0.6"/>
            </pattern>
          </defs>
          <rect width="800" height="500" fill="url(#grid)"/>
          {/* pretend coastline */}
          <path d="M0,380 Q200,320 380,360 T780,340 L800,500 L0,500 Z" fill="#DCE9DA" opacity="0.7"/>
          {/* route line */}
          {points.length >= 2 && (
            <polyline
              points={points.map(p => `${p.x},${p.y}`).join(" ")}
              fill="none" stroke="#C85A32" strokeWidth="2.5" strokeDasharray="6 6" strokeLinecap="round"
            />
          )}
          {/* pins */}
          {points.map((p, i) => (
            <g key={p.id} transform={`translate(${p.x}, ${p.y})`}>
              <circle r="14" fill="#C85A32" opacity="0.15"/>
              <circle r="9" fill="#C85A32"/>
              <text y="3" textAnchor="middle" fontSize="10" fill="white" fontFamily="JetBrains Mono, monospace" fontWeight="700">{i+1}</text>
            </g>
          ))}
          {/* labels */}
          {points.map((p, i) => (
            <g key={"lbl"+p.id} transform={`translate(${p.x + 14}, ${p.y - 6})`}>
              <text fontSize="11" fill="#1c1917" fontFamily="Plus Jakarta Sans, sans-serif" fontWeight="600">{p.title}</text>
              {p.location_name && <text y="14" fontSize="9" fill="#6b7280" fontFamily="Plus Jakarta Sans, sans-serif">{p.location_name}</text>}
            </g>
          ))}
        </svg>
        {pinned.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center bg-white/90 backdrop-blur px-6 py-4 rounded-xl border border-stone-200">
              <MapPin className="mx-auto text-stone-400 mb-1"/>
              <div className="text-sm text-stone-600">No pinned activities for Day {activeDay}.</div>
              <div className="text-xs text-stone-500 mt-1">Add lat/lng to activities to see them here.</div>
            </div>
          </div>
        )}
      </div>

      {points.length > 0 && (
        <ol className="space-y-2">
          {points.map((p, i) => (
            <li key={p.id} className="flex items-center gap-3 p-3 rounded-lg bg-white border border-stone-200">
              <div className="w-7 h-7 rounded-full bg-[#C85A32] text-white font-mono-code text-xs flex items-center justify-center">{i+1}</div>
              <div className="flex-1">
                <div className="font-semibold">{p.title}</div>
                <div className="font-mono-code text-xs text-stone-500">{p.location_name} · {p.lat?.toFixed(3)}, {p.lng?.toFixed(3)}</div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
